import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { anchorAt, recallScroll, rememberScroll, restoreTarget } from '../utils/scrollMemory';

// The browser's default history.scrollRestoration is 'auto', which resets
// EVERY scrollable element on the page on each client-side navigation - not
// just window, but the fixed sidebar's own overflow-y-auto list too, even
// though no app code ever touches its scrollTop. That's what made scrolling
// down the nav, clicking a link, and landing back at the top of the nav feel
// like a bug with no visible cause. 'manual' hands scroll restoration
// entirely to us: this effect still resets the WINDOW to the top of the new
// page (the intended behavior for content), while every other scrollable
// region - the nav rail included - simply keeps whatever position it was
// already at, because nothing forces it back to zero anymore.
if (typeof window !== 'undefined' && window.history) {
    try {
        window.history.scrollRestoration = 'manual';
    } catch {
        // Older/non-browser environments (some SSR/test hosts) don't
        // implement the property - harmless to skip, window.scrollTo
        // below still handles the intended page-level reset.
    }
}

// How long a restore keeps trying while the page it returned to grows to its
// full height (a section that renders after the first frame).
// It keeps following the anchor for the whole window, since a section above
// can grow after the first frame lands; the visitor's own input ends it.
const RESTORE_WINDOW_MS = 2000;

export default function ScrollToTop() {
    const location = useLocation();
    const navigationType = useNavigationType();
    const entryKey = useRef(location.key);
    const firstRun = useRef(true);

    // Remember where the window sits on each history entry. 'manual' above
    // means the browser no longer does this, so a back step (the browser's
    // button, or a page's Back control, #4602) would otherwise land at the
    // top of the page the visitor left halfway down.
    // The anchor is read once a frame at most (it costs a hit test).
    useEffect(() => {
        let frame = 0;
        const onScroll = () => {
            const key = entryKey.current;
            rememberScroll(key, window.scrollY);
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                if (key === entryKey.current) rememberScroll(key, window.scrollY, anchorAt());
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    // Switch entries in the commit, before the browser can fire the scroll
    // event the new page's height causes: that event belongs to the new
    // entry, never to the one being left.
    useLayoutEffect(() => {
        entryKey.current = location.key;
    }, [location.key]);

    useEffect(() => {
        const first = firstRun.current;
        firstRun.current = false;
        // Only a back/forward step inside this page load restores. The first
        // render is a fresh arrival (or a reload) and starts at the top.
        const saved = !first && navigationType === 'POP' ? recallScroll(location.key) : null;
        if (saved == null) {
            jumpTo(0);
            return undefined;
        }
        return restoreScroll(saved);
        // Keyed on the path, as before: a query-only change (a filter) keeps
        // the visitor where they are.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    return null;
}

// A route change moves the window at once. index.css sets
// `scroll-behavior: smooth` on <html> for in-page anchors, and a plain
// scrollTo inherits it: the new page then animated up from wherever the old
// one was left, and a restore animated through the page instead of landing
// (#4602, measured in WebKit and Chrome).
function jumpTo(top) {
    try {
        window.scrollTo({ top, left: 0, behavior: 'instant' });
    } catch {
        // An older browser whose ScrollBehavior enum lacks 'instant' throws a
        // TypeError: turn the smooth CSS off for this one jump instead.
        const root = document.documentElement;
        const prev = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        window.scrollTo(0, top);
        root.style.scrollBehavior = prev;
    }
}

// Scroll back to the saved place, and keep it there for a moment while the
// page settles: it may still be growing tall enough to reach, or a section
// above may grow and push the anchor down. The visitor's own wheel, touch, key
// press or pointer down ends it at once.
function restoreScroll(saved) {
    const started = Date.now();
    let frame = 0;
    let stopped = false;
    const stop = () => {
        stopped = true;
        if (frame) cancelAnimationFrame(frame);
        window.removeEventListener('wheel', stop);
        window.removeEventListener('touchstart', stop);
        window.removeEventListener('keydown', stop);
        window.removeEventListener('pointerdown', stop);
    };
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);
    // A scrollbar drag fires no wheel or touch; it starts with a pointer down.
    window.addEventListener('pointerdown', stop, { passive: true });
    const step = () => {
        if (stopped) return;
        const y = restoreTarget(saved);
        if (Math.abs(window.scrollY - y) >= 2) jumpTo(y);
        if (Date.now() - started > RESTORE_WINDOW_MS) {
            stop();
            return;
        }
        frame = requestAnimationFrame(step);
    };
    step();
    return stop;
}

